import { useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { AlertTriangle, ChevronDown, ChevronRight, Lightbulb } from 'lucide-react';

const SOURCE = {
  custom: ['Custom', 'bg-emerald-50 text-emerald-700 border-emerald-200'],
  default: ['Default', 'bg-sky-50 text-sky-700 border-sky-200'],
  industry: ['Industry default', 'bg-sky-50 text-sky-700 border-sky-200'],
  fuzzy: ['Default (name match)', 'bg-sky-50 text-sky-700 border-sky-200'],
  fallback: ['Unmapped', 'bg-amber-50 text-amber-700 border-amber-200'],
};

const slug = s => s.replace(/[^a-z0-9]+/gi, '-').toLowerCase();

function AccountCell({ account }) {
  return (
    <div className="min-w-0">
      <p className="text-sm text-foreground truncate" data-testid="row-account">
        {account.code && <span className="font-mono text-xs text-muted-foreground mr-1.5">{account.code}</span>}{account.name}
      </p>
      {account.type && <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{account.type}</p>}
    </div>
  );
}

function Advice({ row }) {
  if (!row.flags.length) return null;
  return (
    <div className="mt-2 space-y-1" data-testid="row-flags">
      {row.flags.map(f => (
        <p key={f.code + f.text} className="text-[11px] text-amber-700 flex gap-1.5"><AlertTriangle size={12} className="mt-0.5 shrink-0" />{f.text}</p>
      ))}
      {row.suggestion ? (
        <p className="text-[11px] text-violet-700" data-testid="row-suggestion">
          <Lightbulb size={12} className="inline mr-1.5 -mt-0.5" />
          Suggested (review only): <b>{row.suggestion.code} {row.suggestion.name}</b> — {row.suggestion.reason}
        </p>
      ) : (
        <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200" data-testid="row-needs-review">Needs Review</Badge>
      )}
    </div>
  );
}

export function MappingReviewRow({ row, onEdit }) {
  const [label, cls] = SOURCE[row.source] || SOURCE.default;
  return (
    <div className="grid grid-cols-12 gap-3 px-4 py-3 items-start hover:bg-muted/20 transition-colors" data-testid={`review-row-${slug(row.key)}`}>
      <div className="col-span-5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-foreground" data-testid="row-label">{row.label}</span>
          <Badge variant="outline" className={`text-[10px] ${cls}`} data-testid="row-source">{label}</Badge>
        </div>
        {row.variants.length > 1 && (
          <p className="text-[11px] text-muted-foreground mt-1" data-testid="row-variants">
            Spellings: {row.variants.map(v => `${v.category} (${v.count})`).join(', ')}
          </p>
        )}
        <Advice row={row} />
      </div>
      <div className="col-span-4"><AccountCell account={row.account} /></div>
      <div className="col-span-2 text-right">
        <p className="text-sm font-medium" data-testid="row-count">{row.count.toLocaleString()}</p>
        <p className="text-[10px] text-muted-foreground">txns</p>
      </div>
      <div className="col-span-1 text-right">
        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => onEdit(row.label.toLowerCase(), row.account.name)} data-testid={`review-edit-${slug(row.key)}`}>Edit</Button>
      </div>
    </div>
  );
}

export function SynonymBundleRow({ account, rows }) {
  const [open, setOpen] = useState(false);
  const id = slug(account.code || account.name || 'x');
  return (
    <div className="px-4 py-2.5" data-testid={`synonym-bundle-${id}`}>
      <button type="button" className="w-full grid grid-cols-12 gap-3 items-center text-left" onClick={() => setOpen(o => !o)} data-testid={`synonym-toggle-${id}`}>
        <span className="col-span-5 text-sm text-muted-foreground flex items-center gap-1.5">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          {rows[0].label}{rows.length > 1 && <span className="text-xs text-violet-600" data-testid="synonym-count">+{rows.length - 1} synonyms</span>}
        </span>
        <span className="col-span-4"><AccountCell account={account} /></span>
        <span className="col-span-3 text-right text-[11px] text-muted-foreground">unused default</span>
      </button>
      {open && (
        <p className="mt-2 ml-5 text-[11px] text-muted-foreground" data-testid={`synonym-list-${id}`}>{rows.map(r => r.label).join(' · ')}</p>
      )}
    </div>
  );
}
